import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { cn } from '@/utils/classname';
import { formatUSD } from '@/utils/formatCurrency.util';

interface AISpendMeterProps {
  /** Marked-up spend so far this cycle (AIBudgetStatus.current_usage). */
  currentUsage: number;
  /** Monthly cap (AIBudgetStatus.max_limit). */
  maxLimit: number;
  /** Whether a monthly limit is configured (AIBudgetStatus.set). */
  hasLimit: boolean;
  /** Render the limit-reached (critical) visual even when usage hasn't strictly exceeded the cap — used by the budget-exhausted modal, where the backend blocks new hunts at a >=90% reserve threshold. AISpendCard omits this so its presentation is unchanged. */
  atLimit?: boolean;
  className?: string;
}

/**
 * AISpendMeter — the shared, purely presentational "spend-vs-cap" meter:
 * a capped progress bar, a percent caption (over-limit shows the warning
 * icon + "Over limit"), and a promoted `formatUSD` figure with an
 * "of $X monthly" sub-caption.
 *
 * Extracted verbatim from AISpendCard so the budget-exhausted modal can
 * render the identical visualization (single source of truth — mandated
 * coupling rather than coincidental similarity). AISpendCard omits the
 * `atLimit` prop, so its presentation stays byte-identical; the
 * budget-exhausted modal passes `atLimit` to render the limit-reached
 * (critical) state at the >=90% reserve threshold. It owns no data
 * fetching, no title, and no edit affordances — callers supply the
 * heading and figures.
 */
export const AISpendMeter: React.FC<AISpendMeterProps> = ({
  currentUsage,
  maxLimit,
  hasLimit,
  atLimit = false,
  className,
}) => {
  const percent = !hasLimit
    ? 0
    : maxLimit > 0
      ? Math.min(100, (currentUsage / maxLimit) * 100)
      : 100;
  const overLimit = hasLimit && currentUsage > maxLimit;
  const showCritical = overLimit || (hasLimit && atLimit);

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-between gap-6 sm:flex-row sm:items-center',
        className
      )}
    >
      {/* LEFT: capped meter + percent caption */}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {hasLimit && (
          <div
            className="bg-chariot-background-secondary h-2 w-full max-w-xs overflow-hidden rounded-full"
            role="progressbar"
            aria-valuenow={Math.round(percent)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Spend ${percent.toFixed(0)}% of monthly limit`}
            data-testid="ai-spend-progress"
          >
            <div
              className={cn(
                'h-full rounded-full transition-[width] duration-300 ease-out',
                showCritical
                  ? 'bg-chariot-status-critical'
                  : 'bg-chariot-brand-primary'
              )}
              style={{ width: percent > 0 ? `${Math.max(2, percent)}%` : '0%' }}
            />
          </div>
        )}
        {hasLimit &&
          (overLimit ? (
            <div
              className="text-chariot-text-red flex items-center gap-1 text-xs font-medium"
              data-testid="ai-spend-overlimit"
            >
              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
              <span data-testid="ai-spend-percent">
                {percent.toFixed(0)}% · Over limit
              </span>
            </div>
          ) : atLimit ? (
            <div
              className="text-chariot-text-red flex items-center gap-1 text-xs font-medium"
              data-testid="ai-spend-overlimit"
            >
              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
              <span data-testid="ai-spend-percent">
                {percent.toFixed(0)}% used · Limit reached
              </span>
            </div>
          ) : (
            <span
              className="text-chariot-text-secondary text-xs"
              data-testid="ai-spend-percent"
            >
              {percent.toFixed(0)}% used
            </span>
          ))}
        {!hasLimit && (
          <span className="text-chariot-text-secondary text-xs">
            No monthly limit
          </span>
        )}
      </div>

      {/* RIGHT: promoted spend figure + limit caption */}
      <div className="flex shrink-0 flex-col items-start gap-1 text-right tabular-nums sm:items-end">
        <span
          className={cn(
            'text-lg font-semibold tabular-nums',
            showCritical ? 'text-chariot-text-red' : 'text-chariot-text-primary'
          )}
          data-testid="ai-spend-usage-value"
        >
          {formatUSD(currentUsage)}
        </span>
        <span
          className="text-chariot-text-secondary text-xs"
          data-testid="ai-spend-limit-value"
        >
          {hasLimit ? `of ${formatUSD(maxLimit)} monthly` : 'no monthly limit'}
        </span>
      </div>
    </div>
  );
};
